import { useRef, useEffect, Fragment } from 'react'
import { Trans } from 'react-i18next'
import WarningIcon from '@mui/icons-material/WarningAmberRounded'
import ArrowLeftIcon from '@mui/icons-material/ArrowBackIos'
import ArrowRightIcon from '@mui/icons-material/ArrowForwardIos'

import { useDateFormatter } from '../../../utils/date'
import { mergeClassName } from '../../../utils/mergeClassName'
import {
  useGetQuoteConstraints,
  useGetQuoteJobDate,
  useGetQuoteJobLabel,
  useGetQuoteJobPrice,
  type Quote,
} from '../../../modules/quotes'
import { useBreakpoint } from '../../../modules/theme'
import Price from '../../Price'
import { useQuoteContext } from '../QuoteComponent.context'
import Job from './Job'

type JobsProps = {
  quote: Quote
  className?: string
}

const Jobs: React.FC<JobsProps> = ({
  quote,
  className,
}) => {
  const {
    open,
    promoCode,
    multiJobs,
    selectedJob,
    setSelectedJob,
    jobId,
    docTop = 0,
    darkBackground = false,
  } = useQuoteContext()
  const containerRef = useRef<HTMLDivElement>(null)
  const formatDate = useDateFormatter()
  const getQuoteJobPrice = useGetQuoteJobPrice()
  const getQuoteJobLabel = useGetQuoteJobLabel()
  const getQuoteJobDate = useGetQuoteJobDate()
  const getQuoteConstraints = useGetQuoteConstraints()
  const useTabs = useBreakpoint('md')

  useEffect(() => {
    if (!containerRef.current || useTabs || !open) {
      return
    }
    containerRef.current.scrollIntoView({ behavior: 'smooth' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedJob])

  /**
   * whenever the promo code changes, reset the selected job
   */
  useEffect(() => {
    setSelectedJob(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promoCode])

  if (!open) {
    return null
  }

  if (!multiJobs) {
    const jobIndex = jobId ? quote.jobs.findIndex((job) => job.jobId === jobId) : 0
    return (
      <div className={mergeClassName(
        'border-t',
        className,
      )}
      >
        <Job
          quote={quote}
          jobIndex={jobIndex}
          className="py-6"
        />
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      className="py-6"
    >
      <div
        className={mergeClassName(
          'sticky z-[100] flex items-center justify-between border-b bg-white md:static md:flex-wrap md:justify-start md:gap-4',
          darkBackground && 'mx-[-30px] px-4 md:px-0 md:mx-0 md:bg-transparent',
          className,
        )}
        style={{ top: docTop }}
      >
        { quote.jobs.map((job, index) => {
          const active = selectedJob === index
          const first = selectedJob === 0
          const last = selectedJob === quote.jobs.length - 1
          const showPrevious = quote.jobs.length > 1 && !first
          const showNext = quote.jobs.length > 1 && !last
          const { price } = getQuoteJobPrice(quote, index, { promoCode })
          const hasConstraints = getQuoteConstraints(quote, index).length > 0
          const JobDate = () => <span>{ formatDate(getQuoteJobDate(job), { showYear: false, shortMonth: true, shortDay: true }) }</span>

          return (
            <Fragment key={job.jobId}>
              <div
                className={mergeClassName(
                  'hidden opacity-50 bg-primary text-white items-center justify-center rounded-full p-2 size-[30px] xs:size-[35px]',
                  active && 'flex md:hidden',
                  showPrevious && 'opacity-100',
                )}
                onClick={showPrevious ? () => { setSelectedJob(index - 1) } : undefined}
              >
                <ArrowLeftIcon className="mr-[-5px] !text-[18px] xs:mr-[-7px] xs:!text-[22px]" />
              </div>
              <div
                className={mergeClassName(
                  'border-b-2 md:border-0 md:border-b-2 m-2 md:m-0 cursor-pointer px-4 md:px-6 py-3 text-gray-500 font-semibold hover:border-b-2 hover:border-zinc-600 border-transparent leading-tight rounded-xl md:rounded-none md:rounded-t-xl hover:bg-gray-50 transition-all duration-150',
                  active && 'border-primary hover:border-primary bg-gray-100 hover:bg-gray-100',
                  darkBackground && 'bg-transparent',
                  darkBackground && active && 'bg-white shadow-lg hover:bg-white/40',
                  !active && 'hidden md:block',
                )}
                onClick={() => { setSelectedJob(index) }}
              >
                <div className="flex items-center gap-2">
                  <div>
                    <div className={mergeClassName(
                      active && 'text-primary text-sm md:text-base',
                    )}
                    >
                      <Trans
                        i18nKey="quotes.quote.multiJobTitle"
                        values={{
                          day: index + 1,
                          nbDays: quote.jobs.length,
                        }}
                      >
                        <JobDate />
                      </Trans>
                    </div>
                    <div className={mergeClassName(
                      'text-sm text-gray-400 font-normal',
                      active && 'text-gray-600',
                    )}
                    >
                      { getQuoteJobLabel(job) }
                      <span className="text-gray-400"> • <Price amount={price} compact /></span>
                    </div>
                  </div>
                  { hasConstraints && (
                    <div>
                      <WarningIcon className="!text-[26px] text-yellow-700" />
                    </div>
                  ) }
                </div>
              </div>
              <div
                className={mergeClassName(
                  'hidden opacity-50 bg-primary text-white items-center justify-center rounded-full p-2 size-[30px] xs:size-[35px]',
                  active && 'flex md:hidden',
                  showNext && 'opacity-100',
                )}
                onClick={showNext ? () => { setSelectedJob(index + 1) } : undefined}
              >
                <ArrowRightIcon className="!text-[18px] xs:!text-[22px]" />
              </div>
            </Fragment>
          )
        }) }
      </div>
      <Job
        quote={quote}
        jobIndex={selectedJob}
        className="mx-2 pt-4"
      />
    </div>
  )
}

export default Jobs
